/**
 * Theme — app-wide light/dark mode. Dark is a clean flip of the surface + text
 * tokens (see theme.css [data-theme='dark']) that keeps the brand green. The
 * provider writes data-theme onto <html> so every page adapts, and remembers
 * the choice in localStorage.
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Icon } from '../components/Icon';
import { buzz } from './haptics';

type Theme = 'light' | 'dark';
const STORAGE_KEY = 'ldg-theme';

const ThemeContext = createContext<{ theme: Theme; toggle: () => void }>({
  theme: 'light',
  toggle: () => {},
});

function initialTheme(): Theme {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'dark' || saved === 'light') return saved;
  } catch {
    // localStorage getter itself can throw (Safari "Block All Cookies").
  }
  return 'light';
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(initialTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // localStorage can throw in private mode; the attribute still applied.
    }
  }, [theme]);

  const toggle = useCallback(() => {
    buzz(10);
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
  }, []);

  return <ThemeContext.Provider value={{ theme, toggle }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}

/** iconOnly: just the sun/moon glyph, for compact icon rows (the family
 * hero's collapsed corner, Bobbie 2026-08-19 night: "I'm just trying to
 * collapse that upper section to be smaller"). The aria-label carries the
 * words either way. */
export function ThemeToggle({ style, iconOnly = false }: { style?: React.CSSProperties; iconOnly?: boolean }) {
  const { theme, toggle } = useTheme();
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        // 44 is the app-wide tap target floor (index.css --tap-target). An
        // inline 40 here overrode it, so the labelled toggle on the Landing
        // rendered 40px tall at every width. Measured 40 -> 44 at 390 and
        // 1280 on 2026-08-14. The Command Center mount passes its own
        // style and already said 44, so it is unchanged.
        minHeight: 44,
        padding: '0 14px',
        borderRadius: 999,
        border: '1px solid var(--line)',
        background: 'linear-gradient(180deg, var(--paper) 0%, var(--off-white) 100%)',
        color: 'var(--ink)',
        fontFamily: 'var(--font-body)',
        fontWeight: 800,
        fontSize: 13,
        cursor: 'pointer',
        boxShadow: 'var(--shadow-md)',
        ...style,
      }}
    >
      <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={18} />
      {!iconOnly && (theme === 'dark' ? 'Light' : 'Dark')}
    </button>
  );
}
